/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { categorySchema } from "../core/Schema";
import { useFormik } from "formik";
import { useAuth } from "../../../auth";
import { checkDuplicateCategory, createCategory } from "../../dish/core/_requests";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CloseButton   from "../../../component/core/Buttons";



interface CategoryModalProps {
  showModal: boolean;
  handleModal: () => void;
}
interface CategoryAdd {
  chefId: string | undefined;
  name: string;
  description: string;
  Image: any;
  formImage: any;
  createdBy: any;
  updatedBy: any;
}

const initialValues: CategoryAdd = {
  chefId: "",
  name: "",
  description: "",
  Image: "",
  formImage: "",
  createdBy: "",
  updatedBy: "",
};
const CategoryModal: React.FC<CategoryModalProps> = ({
  showModal,
  handleModal,
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const chefId = currentUser?.uuid;
  const [formImage, setFormImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);
  const [Duplicate, setDuplicate] = useState("");
  const [duplicateCheck, setDuplicateCheck] = useState(false);
  const [data, setData] = useState<CategoryAdd>(initialValues);
  const formik = useFormik<CategoryAdd>({
    initialValues,
    validationSchema: categorySchema,
    onSubmit: async (values, actions) => {
      if (duplicateCheck) {
        return;
      }

      values.chefId = chefId;
      values.createdBy = currentUser?.chefId;

      actions.resetForm({
        values: initialValues,
      });
            const updatedData = Object.assign(data, values);
            //
      const formData = new FormData();
      if (formImage) formData.append("icon", formImage);
      Object.entries(updatedData).forEach(([key, value]) =>
        formData.append(key, value)
      );

      setLoading(true);
      createCategory(formData).then((res) => {
        if (res?.status === true) {
          closeCategory();
          setLoading(false);
          toast.success(res?.message, { autoClose: 5000 });
          navigate("/dish/categorysuccess", { replace: true });
          
          setLoading(false);
        } else {
          setLoading(false);
        }
      });
    },
  });

  const handleErrorMessageClose = () => {
    setErrorMessage("");
    setData({
      ...data,
      Image: null,
    });
    // Reset the file input value
    if (fileInputRef.current) {
      (fileInputRef.current as any).value = "";
    }
  };

  useEffect(() => {
    if (formik.values.name.trim() !== "") {
      checkDuplicateCategory(formik.values.name).then((response) => {
        if (response?.isDuplicate === true) {
          setDuplicate("Category name already exists");
          setDuplicateCheck(true);
        } else {
          setDuplicate("");
          setDuplicateCheck(false);
        }
      });
    }
  }, [formik.values.name]);

  const closeCategory = () => {
    handleModal();
    setFormImage(null);
    setDuplicate("");
    setDuplicateCheck(false);
    formik.resetForm(); // Reset the form when the modal is closed
  };

  const imageHandler = (e: { target: { files: any } }) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setData({
          ...data,
          Image: reader.result,
        });
      }
    };

    const files = e.target.files;
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

    if (files && !allowedTypes.includes(files[0].type)) {
      setErrorMessage(
        "Invalid file type. Allowed types are JPEG, PNG and JPG."
      );
      setFormImage(null);
    } else if (files && files[0].size > 1024 * 1024) {
      // Check if file size exceeds 1MB
      setErrorMessage("File size should be less than 1MB.");
      setFormImage(null);
    } else {
      setErrorMessage(""); // Clear the error message
      if (files) {
        setFormImage(files[0]);
        reader.readAsDataURL(files[0]);
      }
    }
  };
  return (
    <div>
      <Modal show={showModal} onHide={closeCategory}>
        <Modal.Header closeButton>
          <Modal.Title>Add Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Add your category form or content here */}
          <form
            onSubmit={formik.handleSubmit}
            noValidate
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          >
            <div className="col-12 mb-3 mt-3">
              <label htmlFor="" className="form-label">
                Category Image (optional)
              </label>
              <div className="file-input mt-1">
                <input
                  type="file"
                  name="file"
                  id="file"
                  onChange={imageHandler}
                  ref={fileInputRef}
                />
                <label htmlFor="file">
                  <i className="material-symbols-outlined">upload</i>
                  <span> Upload Category Image </span>
                </label>
              </div>
              <span className="file-info">
                Supported file type (.jpg, .png, .jpeg) Max file size:1MB
              </span>
              {data?.Image && (
                <>
                  {" "}
                  <img src={data?.Image} height="100" width="100" />
                  <button
                    className="btn p-0 close d-inline-flex "
                    onClick={handleErrorMessageClose}
                  >
                    <i className="material-symbols-outlined fs-18">close</i>
                  </button>
                </>
              )}
              {errorMessage && (
                <div style={{ color: "red" }} className="custom-error-messages">
                  {errorMessage}

                  <button
                    className="btn p-0 close d-inline-flex "
                    onClick={handleErrorMessageClose}
                  >
                    <i className="material-symbols-outlined fs-18">close</i>
                  </button>
                </div>
              )}
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="" className="form-label require ">
                Category
              </label>
              <input
                type="text"
                className={clsx(
                  "form-control",
                  {
                    "is-invalid": formik.touched.name && formik.errors.name,
                  },
                  {
                    "is-valid": formik.touched.name && !formik.errors.name,
                  }
                )}
                placeholder="Enter Your Category"
                {...formik.getFieldProps(`name`)}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.name}</span>
                </div>
              )}
              <span style={{ color: "red" }}>{Duplicate ? Duplicate : ""}</span>
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="" className="form-label">
                Description (optional)
              </label>
              <input
                type="textarea"
                className={clsx(
                  "form-control",
                  {
                    "is-invalid":
                      formik.touched.description && formik.errors.description,
                  },
                  {
                    "is-valid":
                      formik.touched.description && !formik.errors.description,
                  }
                )}
                placeholder="Enter Your Description"
                {...formik.getFieldProps(`description`)}
              />
              {formik.touched.description && formik.errors.description && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.description}</span>
                </div>
              )}
            </div>
              <CloseButton
                  onClose={closeCategory}
                    onSubmit={formik.handleSubmit}
                  loading={loading}
                   disabled={formik.isSubmitting || !formik.isValid || duplicateCheck}
             />
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CategoryModal;
